<script setup lang="ts">
import { Order, PaginationData, Statuses, TotalByStatus } from '@/@types/order'
import { Workshop } from '@/@types/workshop'
import Event from '@/assets/main/event.webp'
import EventDetail from '@/components/shared/admin/orders/EventDetail.vue'
import Table from '@/components/shared/admin/orders/List.vue'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import { useExpiredWorkshopDialog } from '@/composables/admin/useExpiredWorkshopDialog'
import { formatDateFull } from '@/helpers/format-date'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { ArrowLeftCircle, Calendar, Ticket } from 'lucide-vue-next'
import { provide } from 'vue'

const { id, status, workshop, pagination } = defineProps<{
    workshops?: Workshop[]
    orders?: Order[]
    workshop?: Workshop
    orders_by_status?: TotalByStatus
    statuses: Statuses
    id?: number
    status?: string
    pagination: PaginationData
}>()

const params = useUrlSearchParams()
const { selectedWorkshop, showExpiredConfirmation, handleWorkshopClick, closeExpiredConfirmation, onExpiredDialogClose, confirmExpiredNavigation } =
    useExpiredWorkshopDialog()

provide('params', params)
provide('admin', true)
provide('workshop_id', workshop?.id || 1)
</script>

<template>
    <Head :title="$t('Manage orders')" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold">
                <Link href="/admin/orders" v-if="workshop"> <ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" /> </Link>

                {{ $t('Manage orders') }}
            </Title>

            <section v-if="workshop && orders" class="flex flex-col gap-6">
                <EventDetail :orders_by_status="orders_by_status" :workshop="workshop" />

                <Table
                    :orders="orders"
                    :pagination="pagination"
                    :statuses="statuses"
                    :status="status"
                    :id="id"
                    :workshop-id="workshop.id"
                    :isAdmin="true"
                />
            </section>

            <section class="grid grid-cols-1 gap-6 lg:grid-cols-3" v-else>
                <button
                    v-for="workshop in workshops"
                    :key="workshop.id"
                    type="button"
                    class="cursos-pointer! w-full text-left"
                    @click="handleWorkshopClick(workshop, 'orders')"
                >
                    <Card class="flex h-auto flex-col gap-6 rounded-2xl border-none shadow-md">
                        <CardHeader class="relative h-64 w-full rounded-2xl xl:h-80 2xl:h-90">
                            <img :src="Event" alt="" class="absolute inset-0 h-full w-full rounded-2xl object-cover p-2" />
                        </CardHeader>
                        <CardContent class="flex flex-col gap-4">
                            <span class="font-inter text-sm text-cyan-600 uppercase">
                                {{ $t('Forum') }}
                            </span>
                            <CardTitle class="font-sans text-xl text-gray-800">
                                {{ $t(workshop.description) }}
                            </CardTitle>
                            <CardDescription class="font-sans text-base text-gray-600">
                                {{
                                    $t(
                                        `Participate in the event that transforms Brazilian logistics and shapes the future of the supply chain. At the ILOS Forum, you will find the main...`,
                                        { event_description: $t(workshop.description) },
                                    )
                                }}
                            </CardDescription>
                            <hr />
                        </CardContent>
                        <CardFooter class="flex flex-col items-start gap-4 text-gray-600">
                            <div class="flex gap-2">
                                <Calendar />
                                {{ formatDateFull(workshop.start, $i18n.locale) }}
                                -
                                {{ formatDateFull(workshop.end, $i18n.locale) }}
                            </div>
                            <div class="flex gap-2" v-if="workshop.mailings_count">
                                <Ticket />
                                {{ workshop.mailings_count }} {{ $t('Tickets purchased') }}
                            </div>
                        </CardFooter>
                    </Card>
                </button>

                <AlertDialog :open="showExpiredConfirmation" @update:open="(isOpen) => !isOpen && onExpiredDialogClose()">
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{{ $t('Expired workshop management warning') }}</AlertDialogTitle>
                            <AlertDialogDescription>
                                {{
                                    $t(
                                        'You are about to manage {workshop_description}, whose validity period has already expired. Do you really want to continue?',
                                        { workshop_description: $t(selectedWorkshop?.description ?? '') },
                                    )
                                }}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel @click="closeExpiredConfirmation">{{ $t('No') }}</AlertDialogCancel>
                            <AlertDialogAction @click="confirmExpiredNavigation('orders')">{{ $t('Yes') }}</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </section>
        </main>
    </LightLayout>
</template>
